import React, { useState } from 'react'
import { useCart } from '../context/CartContext'
import { useAnnouncement } from '../context/AnnouncementContext'
import { getOpeningStatus } from '../utils/openingHours'
import '../views/CartPanel.css'
import { openHoursEvents } from '../utils/openHoursEvents'
const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000'

/* ---------------- PHONE UTILS ---------------- */

const formatPhone = (input) => input.replace(/\D/g, '').slice(0, 15)

const isValidPhone = (phone) => /^\d{8,15}$/.test(phone)

/* ---------------- COMPONENT ---------------- */

const CartPanel = ({ menu }) => {
  const { items, totalItems, totalPrice, addItem, removeItem, clearCart } = useCart()
  const {
    announcementActive,
    openDays,
    onlineOrderDays,
  } = useAnnouncement()

  const {
    isOpen: restaurantOpen,
    isOnlineOrderOpen,
  } = getOpeningStatus(new Date(), {
    openDays,
    onlineOrderDays,
    openingHours: menu?.openingHours,
    onlineOrderHours: menu?.onlineOrderHours,
  })

  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const toggleOpen = () => setIsPanelOpen(v => !v)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)
  const isClosed =
    announcementActive ||
    (!restaurantOpen && !isOnlineOrderOpen)
  const [showHoursBubble, setShowHoursBubble] = useState(false)
  const [showHoursHint, setShowHoursHint] = useState(false)
  const handleSend = async (e) => {
    e.preventDefault()
    setError(null)

    if (!items.length) return setError('Panier vide')
    if (!name || !phone) return setError('Nom / téléphone requis')
    if (!isValidPhone(phone)) return setError('Numéro invalide')

    setSending(true)

    try {
      const res = await fetch(`${API_BASE}/api/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, note, items }),
      })

      if (!res.ok) throw new Error('Erreur envoi commande')

      setSent(true)
      clearCart()
    } catch (err) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={`cart-panel ${isPanelOpen ? 'cart-panel--open' : ''}`}>
      {isClosed && (
        <button
          type="button"
          className="cart-closed-bubble"
          onClick={() => window.dispatchEvent(new Event('toggle-hours'))}
        >
          🔴 Fermé — voir les horaires
        </button>
      )}
      <button
        type="button"
        className="cart-panel-toggle"
        onClick={toggleOpen}
      >
        🧺 Panier {totalItems > 0 && (
          <span>
            ({totalItems}) – {totalPrice.toFixed(2).replace('.', ',')}€
          </span>
        )}
      </button>


      {isPanelOpen && (
        <div className="cart-panel-body">

          <h3 className="cart-panel-title">Votre commande</h3>

          {items.length === 0 ? (
            <p>Panier vide</p>
          ) : (
            <ul className="cart-panel-list">

              {items.map((item, index) => {

                const options = item.options ?? {}
                const box = options.boxEnfant
                const clean = (v) => {
                  if (!v) return ''
                  if (typeof v === 'object') return v.name ?? ''
                  return v
                }

                const isBox = !!box?.item?.name

                /* -----------------------------
                   🧠 NOM PRINCIPAL (SOURCE UNIQUE)
                   → ici on évite le doublon Box Enfant
                ------------------------------*/
                const baseName =
                  item.displayName ||
                  item.name?.replace('Box Enfant - Box Enfant', 'Box Enfant') ||
                  item.name

                /* -----------------------------
                   🍔 / 🥩
                ------------------------------*/
                let mainText = ''

                if (isBox) {
                  mainText =
                    `${box.type === 'burger' ? 'Burger' : 'Viande'} : ${box.item.name}`
                } else if (options.burger?.name) {
                  mainText = `Burger : ${options.burger.name}`
                } else if (options.viande?.name) {
                  mainText = `Viande : ${options.viande.name}`
                } else if (Array.isArray(options.brochette)) {
                  const noms = options.brochette
                    .filter(Boolean)
                    .map(b => b.name)
                    .join(' + ')

                  if (noms) {
                    mainText = `Brochettes : ${noms}`
                  }
                }

                /* -----------------------------
                   🍶 SAUCE
                ------------------------------*/
                let sauceText = ''

                if (typeof options.sauce === 'string') {
                  sauceText = `Sauce : ${clean(options.sauce)}`
                } else if (options.sauce) {
                  const parts = []

                  if (options.sauce.frites) {
                    parts.push(`Sauce frites : ${clean(options.sauce.frites)}`)
                  }

                  if (options.sauce.burger) {
                    parts.push(`Sauce burger : ${clean(options.sauce.burger)}`)
                  }

                  sauceText = parts.join(' | ')
                }

                /* -----------------------------
                   🍟 SUPPLÉMENTS
                ------------------------------*/
                const supplementsText =
                  Array.isArray(options.supplements) && options.supplements.length
                    ? `Suppléments : ${options.supplements
                      .map(clean)
                      .filter(Boolean)
                      .join(', ')}`
                    : ''
                const fritesText = options.fritesFormat
                  ? `${options.fritesFormat}`
                  : ''
                /* -----------------------------
                   🧩 ORDER FINAL
                ------------------------------*/
                const optionsText = [

                  sauceText,
                  supplementsText,
                  fritesText,
                ]
                  .filter(Boolean)
                  .join(' | ')

                return (
                  <li key={`${item.id}-${index}`} className="cart-panel-item">

                    <div className="cart-panel-item-main">
                      <span className="cart-panel-item-name">
                        {baseName !== "Sauce" && baseName}

                        {mainText && ` - ${mainText}`}

                        {optionsText && ` - ${optionsText}`}
                      </span>

                      <span className="cart-panel-item-price">
                        {item.price}
                      </span>
                    </div>

                    <div className="cart-panel-item-controls">
                      <button onClick={() => removeItem({ id: item.id, price: item.price })}>
                        −
                      </button>

                      <span>{item.quantity}</span>

                      <button onClick={() => addItem(item)}>
                        +
                      </button>
                    </div>

                  </li>
                )
              })}

            </ul>
          )}

          {items.length > 0 && (
            <div className="cart-panel-total">
              <span>Total</span>
              <span>
                {totalPrice.toFixed(2).replace('.', ',')}€
              </span>
            </div>
          )}

          <form className="cart-panel-form" onSubmit={handleSend}>

            <input
              placeholder="Nom"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <input
              placeholder="Téléphone"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
            />

            <textarea
              placeholder="Commentaire"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            {error && <p className="cart-panel-error">{error}</p>}
            {sent && <p className="cart-panel-success">Commande envoyée 👍</p>}

            <button type="submit" disabled={sending}>
              {sending ? 'Envoi...' : 'Envoyer la commande'}
            </button>

          </form>

        </div>
      )}
    </div>
  )
}

export default CartPanel